<template>
  <div class="page-shell">
    <section class="container">
      <div class="page-intro">
        <p class="eyebrow">Member Profile</p>
        <h1 class="page-title">个人中心</h1>
        <p class="page-description">
          查看个人资料、学习记录和考试历史
        </p>
      </div>

      <div v-if="loading" class="status-card">正在加载会员资料...</div>
      <div v-else-if="errorMessage" class="status-card is-error">{{ errorMessage }}</div>

      <template v-else>
        <!-- 个人资料卡片 -->
        <section class="panel profile-card">
          <div class="profile-card__hero">
            <div class="profile-card__identity">
              <div class="profile-card__avatar">
                {{ avatarText }}
              </div>
              <div>
                <h2>{{ profile.real_name || profile.username || '未命名会员' }}</h2>
                <p>{{ profile.mobile_masked || profile.mobile || '未绑定手机号' }}</p>
                <div class="chip-group profile-card__chips">
                  <span class="pill">状态 {{ profile.status_word || 'normal' }}</span>
                  <span class="pill">会员 ID {{ profile.id || '-' }}</span>
                </div>
              </div>
            </div>

            <div class="profile-card__actions">
              <button class="btn btn--secondary" type="button" @click="goToNotifications">
                站内信
              </button>
              <button class="btn btn--secondary" type="button" @click="goToCloudDrive">
                我的云盘
              </button>
              <button class="btn btn--secondary" type="button" :disabled="loading" @click="loadProfile">
                刷新资料
              </button>
              <button class="btn btn--primary" type="button" :disabled="loggingOut" @click="handleLogout">
                {{ loggingOut ? '退出中...' : '退出登录' }}
              </button>
            </div>
          </div>

          <div class="profile-grid">
            <article class="profile-item">
              <span>单位名称</span>
              <strong>{{ profile.unit_name || '-' }}</strong>
            </article>
            <article class="profile-item">
              <span>用户名</span>
              <strong>{{ profile.username || '-' }}</strong>
            </article>
            <article class="profile-item">
              <span>真实姓名</span>
              <strong>{{ profile.real_name || '-' }}</strong>
            </article>
            <article class="profile-item">
              <span>证件号码</span>
              <strong>{{ profile.certificate_no || '-' }}</strong>
            </article>
            <article class="profile-item">
              <span>最后登录时间</span>
              <strong>{{ profile.last_login_at || '-' }}</strong>
            </article>
            <article class="profile-item">
              <span>最后登录 IP</span>
              <strong>{{ profile.last_login_ip || '-' }}</strong>
            </article>
          </div>
        </section>

        <!-- 标签页导航 -->
        <section class="profile-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="['profile-tab', { 'is-active': activeTab === tab.id }]"
            type="button"
            @click="activeTab = tab.id"
          >
            <span>{{ tab.label }}</span>
            <small v-if="tab.badge">{{ tab.badge }}</small>
          </button>
        </section>

        <!-- 考试历史记录 -->
        <section v-if="activeTab === 'exams'" class="panel exam-records">
          <div class="section-header">
            <h2>考试历史记录</h2>
            <button class="btn btn--secondary btn--sm" :disabled="examLoading" @click="loadExamRecords">
              刷新
            </button>
          </div>

          <div v-if="examError" class="status-card is-error">{{ examError }}</div>
          <div v-if="examLoading" class="status-card">正在加载考试记录...</div>
          <div v-else-if="examRecords.length === 0" class="status-card is-empty">
            暂无考试记录
          </div>
          <div v-else class="exam-list">
            <article
              v-for="record in examRecords"
              :key="record.id"
              class="exam-record-card"
            >
              <div class="exam-record__content" @click="viewExamDetail(record)">
                <div class="exam-record__header">
                  <div class="exam-record__title">
                    <h3>{{ examRecordTitle(record) }}</h3>
                    <p class="exam-record__course">
                      {{ examRecordSubtitle(record) }}
                    </p>
                  </div>
                  <div
                    class="exam-record__score"
                    :class="{
                      'is-pass': record.result === 1,
                      'is-fail': record.result === 2
                    }"
                  >
                    <strong>{{ record.score || 0 }}</strong>
                    <span>/ {{ record.total_score || 100 }}</span>
                  </div>
                </div>

                <div class="exam-record__meta">
                  <span class="meta-item">
                    <span class="meta-icon">🎯</span>
                    及格分: {{ record.pass_score || 0 }}
                  </span>
                  <span class="meta-item">
                    <span class="meta-icon">⏱</span>
                    用时 {{ formatExamDuration(record.started_at, record.finished_at) }}
                  </span>
                  <span class="meta-item">
                    <span class="meta-icon">📅</span>
                    {{ formatDate(record.finished_at || record.created_at) }}
                  </span>
                  <span
                    class="exam-status"
                    :class="{
                      'exam-status--pass': record.result === 1,
                      'exam-status--fail': record.result === 2,
                      'exam-status--pending': record.status === 0
                    }"
                  >
                    {{ record.result_word || record.status_word || '未知' }}
                  </span>
                </div>
              </div>
              <div v-if="record.status === 1" class="exam-record__actions">
                <button
                  class="btn btn--secondary btn--sm"
                  @click.stop="viewScoreAnalysis(record)"
                >
                  🤖 成绩分析
                </button>
              </div>
            </article>
          </div>

          <!-- 分页 -->
          <div v-if="examTotal > examPageSize" class="pagination">
            <button
              class="btn btn--secondary btn--sm"
              :disabled="examPage === 1 || examLoading"
              @click="changeExamPage(examPage - 1)"
            >
              上一页
            </button>
            <span class="pagination-info">
              第 {{ examPage }} / {{ Math.ceil(examTotal / examPageSize) }} 页
            </span>
            <button
              class="btn btn--secondary btn--sm"
              :disabled="examPage >= Math.ceil(examTotal / examPageSize) || examLoading"
              @click="changeExamPage(examPage + 1)"
            >
              下一页
            </button>
          </div>
        </section>
        <section v-else-if="activeTab === 'notifications'" class="panel exam-records">
          <div class="section-header">
            <h2>站内信</h2>
            <button class="btn btn--secondary btn--sm" @click="goToNotifications">
              打开消息中心
            </button>
          </div>
          <div class="status-card">
            当前有 {{ notificationBadgeText }} 条未读消息，可进入消息中心查看详情。
          </div>
        </section>
        <section v-else-if="activeTab === 'notes'" class="panel note-records">
          <div class="section-header">
            <h2>个人笔记</h2>
            <div class="section-header__actions">
              <button class="btn btn--secondary btn--sm" :disabled="noteLoading" @click="loadNotes">
                刷新
              </button>
              <button class="btn btn--primary btn--sm" @click="openNoteEditor()">
                新建笔记
              </button>
            </div>
          </div>

          <div v-if="noteError" class="status-card is-error">{{ noteError }}</div>
          <div v-if="noteLoading" class="status-card">正在加载个人笔记...</div>
          <div v-else-if="noteList.length === 0" class="status-card is-empty">
            暂无个人笔记
          </div>
          <div v-else class="note-list">
            <article
              v-for="note in noteList"
              :key="note.id"
              class="note-card"
            >
              <div class="note-card__body">
                <div class="note-card__title">
                  <h3>{{ note.title || '未命名笔记' }}</h3>
                  <span>{{ note.source_type_word || '手动创建' }}</span>
                </div>
                <p>{{ note.content || note.resource_title || '暂无内容摘要' }}</p>
                <div class="note-card__meta">
                  <span v-if="note.tags" class="pill">{{ note.tags }}</span>
                  <span class="pill">{{ formatDate(note.updated_at || note.created_at) }}</span>
                </div>
              </div>
              <div class="note-card__actions">
                <button class="btn btn--secondary btn--sm" @click="openNoteDetail(note)">
                  查看
                </button>
                <button class="btn btn--secondary btn--sm" @click="openNoteEditor(note)">
                  编辑
                </button>
                <button class="btn btn--secondary btn--sm" @click="deleteNote(note)">
                  删除
                </button>
              </div>
            </article>
          </div>

          <div v-if="noteTotal > notePageSize" class="pagination">
            <button
              class="btn btn--secondary btn--sm"
              :disabled="notePage === 1 || noteLoading"
              @click="changeNotePage(notePage - 1)"
            >
              上一页
            </button>
            <span class="pagination-info">
              第 {{ notePage }} / {{ Math.ceil(noteTotal / notePageSize) }} 页
            </span>
            <button
              class="btn btn--secondary btn--sm"
              :disabled="notePage >= Math.ceil(noteTotal / notePageSize) || noteLoading"
              @click="changeNotePage(notePage + 1)"
            >
              下一页
            </button>
          </div>
        </section>
      </template>
    </section>

    <div v-if="noteDialog.visible" class="note-dialog" @click.self="closeNoteDialog">
      <section class="note-dialog__panel panel">
        <button class="note-dialog__close" type="button" @click="closeNoteDialog">×</button>
        <template v-if="noteDialog.mode === 'view'">
          <div class="note-dialog__header">
            <span class="pill">{{ noteDialog.detail.source_type_word || '个人笔记' }}</span>
            <h3>{{ noteDialog.detail.title || '未命名笔记' }}</h3>
          </div>
          <div v-if="noteDialog.loading" class="status-card">正在加载笔记...</div>
          <div v-else-if="noteDialog.error" class="status-card is-error">{{ noteDialog.error }}</div>
          <div v-else class="note-dialog__content">
            <p>{{ noteDialog.detail.content || '暂无内容' }}</p>
            <div class="note-card__meta">
              <span v-if="noteDialog.detail.resource_title" class="pill">{{ noteDialog.detail.resource_title }}</span>
              <span v-if="noteDialog.detail.tags" class="pill">{{ noteDialog.detail.tags }}</span>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="note-dialog__header">
            <span class="pill">个人笔记</span>
            <h3>{{ noteDialog.form.id ? '编辑笔记' : '新建笔记' }}</h3>
          </div>
          <label class="note-field">
            <span>标题</span>
            <input v-model.trim="noteDialog.form.title" type="text" maxlength="80" placeholder="请输入笔记标题">
          </label>
          <label class="note-field">
            <span>内容</span>
            <textarea v-model.trim="noteDialog.form.content" rows="7" placeholder="记录你的学习要点"></textarea>
          </label>
          <label class="note-field">
            <span>标签</span>
            <input v-model.trim="noteDialog.form.tags" type="text" maxlength="120" placeholder="多个标签可用逗号分隔">
          </label>
          <div v-if="noteDialog.error" class="status-card is-error">{{ noteDialog.error }}</div>
          <div class="note-dialog__actions">
            <button class="btn btn--secondary" type="button" @click="closeNoteDialog">
              取消
            </button>
            <button class="btn btn--primary" type="button" :disabled="noteDialog.saving" @click="submitNote">
              {{ noteDialog.saving ? '保存中...' : '保存笔记' }}
            </button>
          </div>
        </template>
      </section>
    </div>

    <!-- Token 失效弹窗 -->
    <AuthModal :visible="showAuthModal" @relogin="handleRelogin" />
  </div>
</template>

<script>
import { logoutMember } from '@/api/auth'
import { fetchMyExamRecords } from '@/api/courseProcess'
import { fetchMemberNoteDetail, fetchMemberNotes, removeMemberNote, saveMemberNote } from '@/api/memberNote'
import { notificationState } from '@/store/notification'
import { clearSession, hydrateSession, sessionState } from '@/store/session'
import AuthModal from '@/components/AuthModal.vue'
import authErrorHandler from '@/mixins/authErrorHandler'

export default {
  name: 'ProfileView',
  components: {
    AuthModal
  },
  mixins: [authErrorHandler],
  data() {
    return {
      loading: true,
      loggingOut: false,
      errorMessage: '',
      activeTab: 'exams',
      // 考试记录相关
      examLoading: false,
      examError: '',
      examRecords: [],
      examTotal: 0,
      examPage: 1,
      examPageSize: 10,
      noteLoading: false,
      noteError: '',
      noteList: [],
      noteTotal: 0,
      notePage: 1,
      notePageSize: 10,
      noteDialog: {
        visible: false,
        mode: 'edit',
        loading: false,
        saving: false,
        error: '',
        detail: {},
        form: {
          id: 0,
          title: '',
          content: '',
          tags: ''
        }
      }
    }
  },
  computed: {
    profile() {
      return sessionState.profile || {}
    },
    avatarText() {
      const source = this.profile.real_name || this.profile.username || this.profile.mobile || 'M'

      return String(source).slice(0, 1).toUpperCase()
    },
    tabs() {
      return [
        { id: 'exams', label: '考试记录', badge: this.examTotal > 0 ? `${this.examTotal}` : '' },
        { id: 'notes', label: '个人笔记', badge: this.noteTotal > 0 ? `${this.noteTotal}` : '' },
        { id: 'notifications', label: '站内信', badge: notificationState.unreadCount > 0 ? `${notificationState.unreadCount}` : '' }
      ]
    },
    notificationBadgeText() {
      return notificationState.unreadCount > 0 ? notificationState.unreadCount : 0
    }
  },
  created() {
    this.loadProfile()
    this.loadExamRecords()
    this.loadNotes()
  },
  methods: {
    async loadProfile() {
      this.loading = true
      this.errorMessage = ''

      try {
        await hydrateSession()
      } catch (error) {
        if (this.isAuthError(error)) {
          this.handleAuthError()
        } else {
          this.errorMessage = error.message || '会员资料加载失败'
        }
      } finally {
        this.loading = false
      }
    },
    async loadExamRecords() {
      this.examLoading = true
      this.examError = ''

      try {
        const result = await fetchMyExamRecords({
          page: this.examPage,
          page_size: this.examPageSize
        })

        this.examRecords = result.list
        this.examTotal = result.total || result.list.length
        this.examPage = result.page || this.examPage
        this.examPageSize = result.page_size || this.examPageSize
      } catch (error) {
        console.error('加载考试记录失败:', error)
        if (this.isAuthError(error)) {
          this.handleAuthError()
        } else {
          this.examError = error.message || '考试记录加载失败'
        }
      } finally {
        this.examLoading = false
      }
    },
    changeExamPage(page) {
      this.examPage = Math.max(1, page)
      this.loadExamRecords()
    },
    examRecordTitle(record = {}) {
      return record.target_title || record.paper_title || record.description || `考试记录 #${record.id}`
    },
    examRecordSubtitle(record = {}) {
      return [record.course_title, record.target_type_word].filter(Boolean).join(' · ') || '未关联课程'
    },
    async loadNotes() {
      this.noteLoading = true
      this.noteError = ''

      try {
        const result = await fetchMemberNotes({
          page: this.notePage,
          page_size: this.notePageSize
        })
        this.noteList = result.list
        this.noteTotal = result.total || result.list.length
      } catch (error) {
        console.error('加载个人笔记失败:', error)
        if (this.isAuthError(error)) {
          this.handleAuthError()
        } else {
          this.noteError = error.message || '个人笔记加载失败'
        }
      } finally {
        this.noteLoading = false
      }
    },
    changeNotePage(page) {
      this.notePage = Math.max(1, page)
      this.loadNotes()
    },
    resetNoteDialog() {
      this.noteDialog.mode = 'edit'
      this.noteDialog.loading = false
      this.noteDialog.saving = false
      this.noteDialog.error = ''
      this.noteDialog.detail = {}
      this.noteDialog.form = {
        id: 0,
        title: '',
        content: '',
        tags: ''
      }
    },
    openNoteEditor(note = null) {
      this.resetNoteDialog()
      this.noteDialog.visible = true
      this.noteDialog.mode = 'edit'

      if (note) {
        this.noteDialog.form = {
          id: note.id,
          title: note.title || '',
          content: note.content || '',
          tags: note.tags || ''
        }
      }
    },
    async openNoteDetail(note) {
      this.resetNoteDialog()
      this.noteDialog.visible = true
      this.noteDialog.mode = 'view'
      this.noteDialog.loading = true
      this.noteDialog.detail = note || {}

      try {
        this.noteDialog.detail = await fetchMemberNoteDetail(note.id)
      } catch (error) {
        this.noteDialog.error = error.message || '笔记详情加载失败'
      } finally {
        this.noteDialog.loading = false
      }
    },
    closeNoteDialog() {
      this.noteDialog.visible = false
      this.resetNoteDialog()
    },
    async submitNote() {
      if (!this.noteDialog.form.title) {
        this.noteDialog.error = '请填写笔记标题'
        return
      }

      this.noteDialog.saving = true
      this.noteDialog.error = ''

      try {
        await saveMemberNote(this.noteDialog.form)
        this.closeNoteDialog()
        this.loadNotes()
      } catch (error) {
        this.noteDialog.error = error.message || '笔记保存失败'
      } finally {
        this.noteDialog.saving = false
      }
    },
    async deleteNote(note) {
      if (!window.confirm(`确认删除笔记“${note.title || '未命名笔记'}”？`)) {
        return
      }

      try {
        await removeMemberNote(note.id)
        if (this.noteList.length === 1 && this.notePage > 1) {
          this.notePage -= 1
        }
        this.loadNotes()
      } catch (error) {
        alert(error.message || '删除失败')
      }
    },
    viewExamDetail(record) {
      // 跳转到考试结果详情页面
      this.$router.push({
        name: 'exam-result',
        params: { recordId: record.id }
      })
    },
    viewScoreAnalysis(record) {
      // 跳转到成绩分析页面
      this.$router.push({
        name: 'score-analysis',
        params: { recordId: record.id }
      })
    },
    getExamStatusText(record) {
      if (record.status === 0) {
        return '答题中'
      }
      if (record.status === 1) {
        return record.result === 1 ? '已通过' : record.result === 2 ? '未通过' : '已完成'
      }
      return '未知'
    },
    formatExamDuration(startTime, endTime) {
      if (!startTime || !endTime) return '-'

      const start = new Date(startTime)
      const end = new Date(endTime)
      const seconds = Math.floor((end - start) / 1000)

      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)

      if (hours > 0) {
        return `${hours}小时${minutes}分钟`
      }
      return `${minutes}分钟`
    },
    formatDate(dateString) {
      if (!dateString) return '-'

      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    goToCloudDrive() {
      this.$router.push({ name: 'cloud-drive-list' })
    },
    goToNotifications() {
      this.$router.push({ name: 'notifications' })
    },
    async handleLogout() {
      if (this.loggingOut) {
        return
      }

      this.loggingOut = true

      try {
        await logoutMember()
      } catch (error) {
        console.warn(error)
      } finally {
        clearSession()
        this.loggingOut = false
        this.$router.push({ name: 'home' })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.profile-card {
  display: grid;
  gap: 22px;
  padding: 28px;
}

.profile-card__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.profile-card__identity {
  display: flex;
  align-items: center;
  gap: 18px;
}

.profile-card__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 24px;
  background: linear-gradient(135deg, $color-primary 0%, $color-accent 100%);
  color: $color-surface;
  font-size: 28px;
  font-weight: 800;
}

.profile-card__identity p {
  margin-top: 8px;
  color: $color-text-secondary;
}

.profile-card__chips {
  margin-top: 14px;
}

.profile-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.profile-item {
  display: grid;
  gap: 8px;
  padding: 18px;
  border: 1px solid rgba($color-border, 0.94);
  border-radius: 20px;
  background: rgba($color-surface, 0.72);
}

.profile-item span {
  color: $color-text-secondary;
  font-size: 13px;
}

.profile-item strong {
  font-size: 16px;
  word-break: break-all;
}

/* 标签页 */
.profile-tabs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px;
  margin-top: 22px;
}

.profile-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 54px;
  padding: 0 12px 12px;
  border: 0;
  background: transparent;
  color: $color-text-secondary;
  font-size: 18px;
  font-weight: 800;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: $color-primary;
  }

  small {
    color: rgba($color-primary, 0.86);
    font-size: 13px;
  }

  &::after {
    content: '';
    position: absolute;
    right: 12px;
    bottom: 0;
    left: 12px;
    height: 3px;
    border-radius: 999px;
    background: transparent;
    transition: background-color 0.18s ease;
  }

  &.is-active {
    color: $color-primary;

    &::after {
      background: linear-gradient(135deg, $color-primary 0%, $color-accent 100%);
    }
  }
}

/* 考试记录 */
.exam-records {
  padding: 28px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }
}

.section-header__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.exam-list {
  display: grid;
  gap: 16px;
}

.note-records {
  padding: 28px;
}

.note-list {
  display: grid;
  gap: 16px;
}

.note-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 20px;
  border: 1px solid rgba($color-border, 0.94);
  border-radius: 20px;
  background: rgba($color-surface, 0.72);
}

.note-card__body {
  display: grid;
  min-width: 0;
  gap: 10px;
}

.note-card__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  h3 {
    margin: 0;
    color: $color-text-primary;
    font-size: 18px;
  }

  span {
    color: $color-primary;
    font-size: 13px;
    font-weight: 700;
  }
}

.note-card__body p {
  margin: 0;
  color: $color-text-secondary;
  line-height: 1.7;
  word-break: break-word;
}

.note-card__meta,
.note-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.note-card__actions {
  flex-shrink: 0;
  justify-content: flex-end;
}

.note-dialog {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(18, 24, 38, 0.58);
  backdrop-filter: blur(8px);
}

.note-dialog__panel {
  position: relative;
  display: grid;
  width: min(680px, 100%);
  gap: 18px;
  padding: 28px;
}

.note-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: rgba($color-text-primary, 0.08);
  color: $color-text-primary;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.note-dialog__header {
  display: grid;
  gap: 10px;
  padding-right: 42px;

  h3 {
    margin: 0;
    font-size: 24px;
  }
}

.note-dialog__content {
  display: grid;
  gap: 16px;

  p {
    margin: 0;
    color: $color-text-secondary;
    line-height: 1.8;
    white-space: pre-wrap;
  }
}

.note-field {
  display: grid;
  gap: 8px;

  span {
    color: $color-text-secondary;
    font-size: 14px;
    font-weight: 700;
  }

  input,
  textarea {
    width: 100%;
    border: 1px solid $color-border;
    border-radius: 14px;
    padding: 12px 14px;
    color: $color-text-primary;
    font: inherit;
    outline: none;
    resize: vertical;

    &:focus {
      border-color: rgba($color-primary, 0.58);
      box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
    }
  }
}

.note-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.exam-record-card {
  padding: 20px;
  border: 1px solid rgba($color-border, 0.94);
  border-radius: 20px;
  background: rgba($color-surface, 0.72);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba($color-primary, 0.4);
    background: rgba($color-primary-light, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba($color-primary, 0.1);
  }
}

.exam-record__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.exam-record__title {
  flex: 1;

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 6px 0;
    color: $color-text-primary;
  }

  .exam-record__course {
    font-size: 14px;
    color: $color-text-secondary;
    margin: 0;
  }
}

.exam-record__score {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 12px;
  background: rgba($color-border, 0.3);

  strong {
    font-size: 28px;
    font-weight: 800;
    color: $color-text-primary;
  }

  span {
    font-size: 16px;
    color: $color-text-secondary;
  }

  &.is-pass {
    background: rgba(#10b981, 0.1);

    strong {
      color: #10b981;
    }
  }

  &.is-fail {
    background: rgba(#ef4444, 0.1);

    strong {
      color: #ef4444;
    }
  }
}

.exam-record__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: $color-text-secondary;

  .meta-icon {
    font-size: 16px;
  }
}

.exam-status {
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;

  &--pass {
    background: rgba(#10b981, 0.1);
    color: #10b981;
  }

  &--fail {
    background: rgba(#ef4444, 0.1);
    color: #ef4444;
  }

  &--pending {
    background: rgba(#f59e0b, 0.1);
    color: #f59e0b;
  }
}

/* 分页 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
}

.pagination-info {
  font-size: 14px;
  color: $color-text-secondary;
}

.btn--sm {
  min-height: 36px;
  padding: 0 16px;
  font-size: 14px;
}

@media (max-width: 960px) {
  .profile-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .profile-card {
    padding: 20px;
  }

  .profile-card__hero,
  .profile-card__identity {
    flex-direction: column;
    align-items: flex-start;
  }

  .profile-grid {
    grid-template-columns: 1fr;
  }

  .exam-records {
    padding: 20px;
  }

  .note-records {
    padding: 20px;
  }

  .section-header,
  .note-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .note-card__actions,
  .note-dialog__actions {
    justify-content: flex-start;
  }

  .exam-record__header {
    flex-direction: column;
  }

  .exam-record__score {
    align-self: flex-start;
  }
}
</style>
